import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { fadeUp } from '../utils/animations';
import Section from './Section';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiStar, FiChevronDown, FiCheck, FiImage, FiUser } = FiIcons;

// Updated event list based on the new requirements
const EVENT_LIST = [
  "Practical AI for Construction - Bell Construction (Mar 2026)",
  "HBCU Sports & Tech Innovation Summit (Apr 2026)",
  "AI Takeover: \"AI Side Hustles\" (May 2026)",
  "AI Takeover Business Summit (Fall 2026)",
  "AI Masterclass: \"Automate Your Business\" - (Summer 2026)",
  "How to Leverage AI - Asurion (Nov 2025)"
];

export default function SpeakingFeedbackSection() {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const name = e.target.name.value;
    const event = e.target.event.value;
    const title = e.target.title.value;
    const review = e.target.review.value;

    try {
      const { error } = await supabase
        .from('speaking_feedback_1774924928825')
        .insert([
          { 
            name, 
            event_name: event, 
            rating, 
            title, 
            review,
            // A generic placeholder until a real image upload flow is built
            image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80" 
          }
        ]);
        
      if (error) throw error;
      
      setIsSubmitted(true);
      setRating(0);
      setFileName("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("There was an error submitting your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFileName(e.target.files[0].name);
    } else {
      setFileName("");
    }
  };

  return (
    <Section 
      id="feedback" 
      eyebrow="Feedback" 
      title="Speaking Engagement Feedback" 
      subtitle="Were you part of a recent audience? We'd love to hear your thoughts on Dr. Jeff's session. Your feedback helps refine future talks and create more impactful experiences."
    >
      <motion.div 
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="max-w-3xl mx-auto overflow-hidden rounded-[32px] border border-white/10 bg-[linear-gradient(135deg,rgba(59,130,246,0.18),rgba(124,58,237,0.14),rgba(249,115,22,0.14))] p-[1px]"
      >
        <div className="rounded-[31px] bg-[#0b1224]/90 p-8 md:p-10 backdrop-blur-xl">
          {isSubmitted ? (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-10"
            >
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/20 text-blue-400 mb-6 border border-blue-500/30">
                <SafeIcon icon={FiCheck} className="h-8 w-8" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3">Thank you for your feedback!</h3>
              <p className="text-white/70">Your review has been submitted successfully.</p>
              <button 
                onClick={() => setIsSubmitted(false)}
                className="mt-8 px-6 py-2 rounded-full border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 transition-colors"
              >
                Submit another review
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name Input */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-medium text-white/90">
                    Your Name
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-white/50">
                      <SafeIcon icon={FiUser} className="h-4 w-4" />
                    </div>
                    <input 
                      type="text" 
                      id="name" 
                      required
                      placeholder="John Doe"
                      className="w-full rounded-xl border border-white/10 bg-white/5 pl-11 pr-4 py-3 text-white placeholder:text-white/30 focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Image Upload Input */}
                <div className="flex flex-col gap-2">
                  <label htmlFor="image" className="text-sm font-medium text-white/90">
                    Profile Image <span className="text-white/50 text-xs font-normal">(Optional)</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="file" 
                      id="image" 
                      accept="image/*"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="w-full flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white transition-colors focus-within:border-blue-500/50 focus-within:ring-1 focus-within:ring-blue-500/50 hover:bg-white/10">
                      <SafeIcon icon={FiImage} className="h-4 w-4 text-white/50 flex-shrink-0" />
                      <span className="text-sm truncate text-white/70">
                        {fileName || "Choose an image..."}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Dropdown */}
              <div className="flex flex-col gap-2">
                <label htmlFor="event" className="text-sm font-medium text-white/90">
                  Select Event Attended
                </label>
                <div className="relative">
                  <select 
                    id="event" 
                    required
                    defaultValue=""
                    className="w-full appearance-none rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-colors cursor-pointer"
                  >
                    <option value="" disabled>Select an event...</option>
                    {EVENT_LIST.map((event, i) => (
                      <option key={i} value={event} className="bg-[#0b1224] text-white">
                        {event}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-white/50">
                    <SafeIcon icon={FiChevronDown} className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Star Rating */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-white/90">
                  Overall Rating:
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <SafeIcon 
                        icon={FiStar} 
                        className={`h-8 w-8 transition-colors duration-200 ${
                          star <= (hoverRating || rating) 
                            ? "fill-orange-400 text-orange-400" 
                            : "text-white/20"
                        }`} 
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div className="flex flex-col gap-2">
                <label htmlFor="title" className="text-sm font-medium text-white/90">
                  Add A Title:
                </label>
                <input 
                  type="text" 
                  id="title" 
                  required
                  placeholder="Summarize your experience..."
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-colors"
                />
              </div>

              {/* Written Review */}
              <div className="flex flex-col gap-2">
                <label htmlFor="review" className="text-sm font-medium text-white/90">
                  Add a Written Review: 
                  <span className="block text-xs text-white/50 mt-1 font-normal">What did you like or dislike about the presentation or product?</span>
                </label>
                <textarea 
                  id="review" 
                  rows={4}
                  required
                  placeholder="Share your thoughts here..."
                  className="w-full resize-y rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-colors"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button 
                type="submit"
                disabled={rating === 0 || isSubmitting}
                className={`mt-2 w-full rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-all ${
                  rating === 0 || isSubmitting
                    ? "bg-white/10 cursor-not-allowed text-white/50" 
                    : "bg-blue-600 shadow-lg shadow-blue-500/20 hover:bg-blue-500 hover:scale-[1.02] active:scale-[0.98]"
                }`}
              >
                {isSubmitting ? "Submitting..." : "Write A Review"}
              </button>

            </form>
          )}
        </div>
      </motion.div>
    </Section>
  );
}